import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { ProductFormModal } from "../../components/admin/ProductFormModal";
import { ProductQRCodeModal } from "../../components/admin/ProductQRCodeModal";
import { Modal } from "../../components/ui/Modal";
import { useApi } from "../../context/ApiContext";
import { useCurrency } from "../../context/CurrencyContext";
import { getThumbnailUrl, getOriginalImageUrl } from "../../utils/imageUtils";
import { SearchInput } from "../../components/SearchInput";
import { Select } from "../../components/ui/Select";
import { getCountryByCode } from "../../utils/countryCurrencyData";
import { Price } from "../../components/ui/Price";
interface Product {
 _id: string;
 title: string;
 handle: string;
 description?: string;
 status: "active" | "draft" | "archived";
 featured?: boolean;
 basePrice?: number;
 displayPrice?: number; // Price calculated based on admin's location priority
 displayCurrency?: string; // Currency for displayPrice
 inventoryQuantity?: number;
 variants: Array<{
  _id: string;
  name: string;
  price: number;
  compareAtPrice?: number;
  sku: string;
  inventoryQuantity?: number;
 }>;
 variantOptions?: Array<{
  name: string;
  values: Array<{
   value: string;
   inventoryQuantity?: number;
  }>;
 }>;
 images: Array<{
  url: string;
  isPrimary?: boolean;
 }>;
 createdAt: string;
}

export const Products: React.FC = () => {
 const { t } = useTranslation();
 const { api } = useApi();
 const { currency } = useCurrency();
 const [products, setProducts] = useState<Product[]>([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [success, setSuccess] = useState<string | null>(null);
 const [debouncedSearch, setDebouncedSearch] = useState("");
 const [statusFilter, setStatusFilter] = useState<string>("");
 const [countryFilter, setCountryFilter] = useState<string>("");
 const [availableCountries, setAvailableCountries] = useState<Array<{ code: string; name: string }>>([]);
 const [page, setPage] = useState(1);
 const [total, setTotal] = useState(0);
 const [limit] = useState(20);
 const [showProductModal, setShowProductModal] = useState(false);
 const [editingProductId, setEditingProductId] = useState<string | undefined>(undefined);
 const [showQRCodeModal, setShowQRCodeModal] = useState(false);
 const [qrCodeProductId, setQrCodeProductId] = useState<string | null>(null);
 const [qrCodeProductTitle, setQrCodeProductTitle] = useState<string>("");
 const [showImportModal, setShowImportModal] = useState(false);
 const [importFile, setImportFile] = useState<File | null>(null);
 const [importing, setImporting] = useState(false);
 const [importPreview, setImportPreview] = useState<Array<{ handle: string; title: string }>>([]);
 const [importJobId, setImportJobId] = useState<string | null>(null);
 const [importProgress, setImportProgress] = useState<{
   status: string;
   total: number;
   processed: number;
   imported: number;
   skipped: number;
   records: Array<{ handle: string; title: string; status: string; error?: string }>;
 } | null>(null);
 const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

 // Load active job from localStorage on mount
 useEffect(() => {
  const savedJobId = localStorage.getItem("importJobId");
  if (savedJobId) {
   // Check if job is still active
   api.get(`/products/import/status/${savedJobId}`)
    .then((response: any) => {
     const status = response.data;
     if (status.status === "running" || status.status === "pending") {
      setImportJobId(savedJobId);
      setImporting(true);
      setImportProgress({
       status: status.status,
       total: status.total,
       processed: status.processed,
       imported: status.imported,
       skipped: status.skipped,
       records: status.records || [],
      });
      // Resume polling
      pollImportStatus(savedJobId);
     } else {
      // Job completed, failed, or cancelled, remove from localStorage
      localStorage.removeItem("importJobId");
      // Still show progress if cancelled or failed for user feedback
      if (status.status === "cancelled" || status.status === "failed") {
       setImportJobId(savedJobId);
       setImportProgress({
        status: status.status,
        total: status.total,
        processed: status.processed,
        imported: status.imported,
        skipped: status.skipped,
        records: status.records || [],
       });
      }
     }
    })
    .catch(() => {
     // Job not found or error, remove from localStorage
     localStorage.removeItem("importJobId");
    });
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []); // Only run on mount
 const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
 const [bulkActionLoading, setBulkActionLoading] = useState(false);

 const fetchProducts = useCallback(async (currentPage: number = page, currentSearch: string = debouncedSearch, currentStatus: string = statusFilter, currentCountry: string = countryFilter) => {
  try {
   setLoading(true);
   setError(null);
   const params = new URLSearchParams({
    page: currentPage.toString(),
    limit: limit.toString(),
    ...(currentSearch && { search: currentSearch }),
    ...(currentStatus && { status: currentStatus }),
    ...(currentCountry && { countryCode: currentCountry }),
   });
   const response = await api.get(`/products/admin?${params.toString()}`);
   setProducts(response.data.products || []);
   setTotal(response.data.total || 0);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.products.errors.fetch"));
  } finally {
   setLoading(false);
  }
 }, [api, page, limit, debouncedSearch, statusFilter, countryFilter, t]);

 // Handler for when search is ready from SearchInput component
 // Use useRef to ensure callback never changes
 const handleSearchReadyRef = useRef((searchQuery: string) => {
  setDebouncedSearch(searchQuery);
  setPage(1);
 });

 // Update the ref function to use current state setters
 useEffect(() => {
  handleSearchReadyRef.current = (searchQuery: string) => {
   setDebouncedSearch(searchQuery);
   setPage(1);
  };
 }, []);

 const handleSearchReady = useCallback((searchQuery: string) => {
  handleSearchReadyRef.current(searchQuery);
 }, []);

 // Memoize placeholder to prevent SearchInput from rerendering
 const searchPlaceholder = useMemo(() => t("admin.products.searchPlaceholder"), [t]);

 // Fetch available locations/countries for filter
 useEffect(() => {
  const fetchLocations = async () => {
   try {
    const response = await api.get("/locations/admin?isActive=true");
    const locations = response.data.locations || [];
    // Map locations to country codes and names
    const countryOptions = locations.map((loc: any) => {
     const country = getCountryByCode(loc.countryCode);
     return {
      code: loc.countryCode,
      name: country?.name || loc.country || loc.countryCode,
     };
    });
    setAvailableCountries(countryOptions);
   } catch (err: any) {
    console.error("Error fetching locations:", err);
    setAvailableCountries([]);
   }
  };
  fetchLocations();
 }, [api]);

 useEffect(() => {
  fetchProducts(page, debouncedSearch, statusFilter, countryFilter);
 }, [page, debouncedSearch, statusFilter, countryFilter, fetchProducts]);

 const handleDelete = async (productId: string) => {
  if (!window.confirm(t("admin.products.confirmDelete"))) {
   return;
  }

  try {
   // Optimistically remove from UI
   setProducts((prev) => prev.filter((p) => p._id !== productId));
   setTotal((prev) => Math.max(0, prev - 1));
   
   await api.delete(`/products/${productId}`);
   setSelectedIds((prev) => {
    const newSet = new Set(prev);
    newSet.delete(productId);
    return newSet;
   });
   setSuccess(t("admin.products.productDeleted"));
   setTimeout(() => setSuccess(null), 5000);
   // Refetch to ensure consistency
   fetchProducts(page, debouncedSearch, statusFilter, countryFilter);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.products.errors.delete"));
   // Refetch on error to restore correct state
   fetchProducts(page, debouncedSearch, statusFilter, countryFilter);
  }
 };

 const handleStatusChange = async (productId: string, newStatus: string) => {
  try {
   await api.patch(`/products/${productId}`, { status: newStatus });
   setProducts(
    products.map((p) =>
     p._id === productId ? { ...p, status: newStatus as any } : p
    )
   );
   setSuccess(t("admin.products.statusUpdated"));
   setError(null);
   setTimeout(() => setSuccess(null), 5000);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.products.errors.updateStatus"));
   setSuccess(null);
  }
 };

 const handleFeaturedChange = async (productId: string, featured: boolean) => {
  try {
   await api.patch(`/products/${productId}`, { featured });
   setProducts(
    products.map((p) =>
     p._id === productId ? { ...p, featured } : p
    )
   );
   setSuccess(featured ? "Product featured" : "Product unfeatured");
   setError(null);
   setTimeout(() => setSuccess(null), 5000);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to update featured status");
   setSuccess(null);
  }
 };

 const handleCreate = () => {
  setEditingProductId(undefined);
  setShowProductModal(true);
 };

 const handleEdit = (productId: string) => {
  setEditingProductId(productId);
  setShowProductModal(true);
 };

 const handleShowQRCode = (productId: string, productTitle: string) => {
  setQrCodeProductId(productId);
  setQrCodeProductTitle(productTitle);
  setShowQRCodeModal(true);
 };

 const handleModalClose = () => {
  setShowProductModal(false);
  setEditingProductId(undefined);
 };

 const handleProductSaved = () => {
  setShowProductModal(false);
  setEditingProductId(undefined);
  setSuccess(t("admin.products.productSaved"));
  setError(null);
  fetchProducts(page, debouncedSearch);
  // Clear success message after 5 seconds
  setTimeout(() => setSuccess(null), 5000);
 };

 const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  if (!file) return;

  // Don't allow selecting a new file if import is in progress
  if (importing || (importJobId && importProgress && (importProgress.status === "running" || importProgress.status === "pending"))) {
   setError(t("admin.products.importInProgress"));
   return;
  }

  setImportFile(file);
  setError(null);
  setImportPreview([]);
  setImportJobId(null);
  setImportProgress(null);

  try {
   const formData = new FormData();
   formData.append("file", file);

   const response = await api.post("/products/import/preview", formData, {
    headers: {
     "Content-Type": "multipart/form-data",
    },
   });

   setImportPreview(response.data.records || []);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.products.importError"));
   setImportFile(null);
  }
 };

 const handleStartImport = async () => {
  if (!importFile) return;

  try {
   setImporting(true);
   setError(null);
   setSuccess(null);
   const formData = new FormData();
   formData.append("file", importFile);

   const response = await api.post("/products/import/start", formData, {
    headers: {
     "Content-Type": "multipart/form-data",
    },
   });

   const jobId = response.data.jobId;
   setImportJobId(jobId);
   // Store jobId in localStorage so we can resume if modal is closed
   localStorage.setItem("importJobId", jobId);

   // Initialize progress state immediately to show progress UI
   setImportProgress({
    status: "pending",
    total: importPreview.length || 0,
    processed: 0,
    imported: 0,
    skipped: 0,
    records: importPreview.map((p) => ({
     handle: p.handle,
     title: p.title,
     status: "pending" as const,
    })),
   });

   // Start polling immediately
   pollImportStatus(jobId);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.products.importError"));
   setSuccess(null);
   setImporting(false);
  }
 };

 const handleAbortImport = async () => {
  if (!importJobId) return;

  try {
   setError(null);
   await api.post(`/products/import/abort/${importJobId}`);
   
   // Stop polling
   if (pollingTimeoutRef.current) {
    clearTimeout(pollingTimeoutRef.current);
    pollingTimeoutRef.current = null;
   }
   
   // Reset all import state
   setImporting(false);
   setImportJobId(null);
   setImportProgress(null);
   setImportFile(null);
   setImportPreview([]);
   
   // Remove from localStorage
   localStorage.removeItem("importJobId");
   
   // Close modal
   setShowImportModal(false);
   
   // Show success message
   setSuccess(t("admin.products.importCancelled"));
   setTimeout(() => setSuccess(null), 5000);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.products.importAbortError"));
  }
 };

 const pollImportStatus = useCallback(async (jobId: string) => {
  const poll = async () => {
   try {
    const response = await api.get(`/products/import/status/${jobId}`);
    const status = response.data;

    setImportProgress({
     status: status.status,
     total: status.total || 0,
     processed: status.processed || 0,
     imported: status.imported || 0,
     skipped: status.skipped || 0,
     records: status.records || [],
    });

    // Continue polling if job is still running
    if (status.status === "running" || status.status === "pending") {
     pollingTimeoutRef.current = setTimeout(poll, 2000); // Poll every 2 seconds
    } else {
     // Job completed, failed, or cancelled
     setImporting(false);
     pollingTimeoutRef.current = null;
     // Remove jobId from localStorage when completed/failed/cancelled
     localStorage.removeItem("importJobId");
     if (status.status === "completed") {
      // Don't show success message immediately - let user see the final progress
      // Show success after a short delay to allow progress to be visible
      setTimeout(() => {
       setSuccess(t("admin.products.importSuccess"));
       setTimeout(() => setSuccess(null), 5000);
      }, 1000);
      fetchProducts(page, debouncedSearch, statusFilter, countryFilter);
     } else if (status.status === "failed") {
      setError(status.error || t("admin.products.importError"));
     } else if (status.status === "cancelled") {
      setSuccess(t("admin.products.importCancelled"));
      setTimeout(() => setSuccess(null), 5000);
     }
    }
   } catch (err: any) {
    setError(err.response?.data?.error || t("admin.products.importError"));
    setImporting(false);
    pollingTimeoutRef.current = null;
    // Remove jobId from localStorage on error
    localStorage.removeItem("importJobId");
   }
  };

  poll();
 }, [api, t, fetchProducts, page, debouncedSearch]);

 // Handle modal open - resume polling if job is active
 useEffect(() => {
  if (showImportModal && importJobId && !pollingTimeoutRef.current) {
   // Resume polling if modal is opened and job is active
   const status = importProgress?.status;
   if (status === "running" || status === "pending") {
    setImporting(true);
    pollImportStatus(importJobId);
   }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [showImportModal, importJobId]);

 // Cleanup polling on unmount or modal close
 useEffect(() => {
  return () => {
   if (pollingTimeoutRef.current) {
    clearTimeout(pollingTimeoutRef.current);
    pollingTimeoutRef.current = null;
   }
  };
 }, []);

 const handleDownloadTemplate = () => {
  const link = document.createElement("a");
  link.href = "/ushoplify-products-catalog-template.csv";
  link.download = "ushoplify-products-catalog-template.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
 };

 const handleExportCatalog = async () => {
  try {
   setError(null);
   setSuccess(null);
   
   const response = await api.get("/products/export", {
    responseType: "blob",
   });

   // Create blob and download
   const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
   const link = document.createElement("a");
   const url = URL.createObjectURL(blob);
   link.href = url;
   link.download = `catalog-export-${new Date().toISOString().split("T")[0]}.csv`;
   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);
   URL.revokeObjectURL(url);

   setSuccess(t("admin.products.exportSuccess") || "Catalog exported successfully");
   setTimeout(() => setSuccess(null), 5000);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.products.exportError") || "Failed to export catalog");
  }
 };

 const handleSelectAll = (checked: boolean) => {
  if (checked) {
   setSelectedIds(new Set(products.map((p) => p._id)));
  } else {
   setSelectedIds(new Set());
  }
 };

 const handleSelectOne = (id: string, checked: boolean) => {
  const newSelected = new Set(selectedIds);
  if (checked) {
   newSelected.add(id);
  } else {
   newSelected.delete(id);
  }
  setSelectedIds(newSelected);
 };

 const handleBulkStatusChange = async (status: string) => {
  if (selectedIds.size === 0) return;

  try {
   setBulkActionLoading(true);
   setError(null);
   setSuccess(null);
   await api.post("/products/bulk/status", { ids: Array.from(selectedIds), status });
   setSelectedIds(new Set());
   setSuccess(t("admin.products.statusUpdated"));
   setTimeout(() => setSuccess(null), 5000);
   fetchProducts(page, debouncedSearch, statusFilter, countryFilter);
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.products.errors.updateStatus"));
   setSuccess(null);
  } finally {
   setBulkActionLoading(false);
  }
 };

 const handleBulkAction = async (action: string) => {
  if (selectedIds.size === 0) return;

  if (action === "") return; // Placeholder selected, do nothing

  if (action === "clear") {
   setSelectedIds(new Set());
   return;
  }

  // Status changes: active, draft, archived
  if (["active", "draft", "archived"].includes(action)) {
   const statusKey = `setTo${action.charAt(0).toUpperCase() + action.slice(1)}`;
   const statusLabel = t(`admin.products.${statusKey}`) || action;
   if (!window.confirm(t("admin.products.confirmBulkStatusChange", { count: selectedIds.size, status: statusLabel }) || `Change status to "${statusLabel}" for ${selectedIds.size} product(s)?`)) {
    return;
   }
   await handleBulkStatusChange(action);
   return;
  }

  if (action === "duplicate") {
   if (!window.confirm(t("admin.products.confirmBulkDuplicate", { count: selectedIds.size }))) {
    return;
   }
   try {
    setBulkActionLoading(true);
    setError(null);
    setSuccess(null);
    const response = await api.post("/products/bulk/duplicate", { ids: Array.from(selectedIds) });
    setSelectedIds(new Set());
    setSuccess(t("admin.products.productsDuplicated", { count: response.data.duplicatedCount }));
    setTimeout(() => setSuccess(null), 5000);
    fetchProducts(page, debouncedSearch, statusFilter, countryFilter);
   } catch (err: any) {
    setError(err.response?.data?.error || t("admin.products.errors.duplicate"));
   } finally {
    setBulkActionLoading(false);
   }
   return;
  }

  if (action === "delete") {
   if (!window.confirm(t("admin.products.confirmBulkDelete", { count: selectedIds.size }))) {
    return;
   }
   try {
    setBulkActionLoading(true);
    setError(null);
    setSuccess(null);
    const idsToDelete = Array.from(selectedIds);
    
    // Optimistically remove from UI
    setProducts((prev) => prev.filter((p) => !idsToDelete.includes(p._id)));
    setTotal((prev) => Math.max(0, prev - idsToDelete.length));
    
    await api.post("/products/bulk/delete", { ids: idsToDelete });
    setSelectedIds(new Set());
    setSuccess(t("admin.products.productDeleted"));
    setTimeout(() => setSuccess(null), 5000);
    // Refetch to ensure consistency
    fetchProducts(page, debouncedSearch, statusFilter, countryFilter);
   } catch (err: any) {
    setError(err.response?.data?.error || t("admin.products.errors.delete"));
    setSuccess(null);
    // Refetch on error to restore correct state
    fetchProducts(page, debouncedSearch, statusFilter, countryFilter);
   } finally {
    setBulkActionLoading(false);
   }
   return;
  }
 };

 const totalPages = Math.ceil(total / limit);

 if (loading && products.length === 0) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <div className="flex items-center justify-between">
     <Heading size="xl">{t("admin.products.title")}</Heading>
     <div className="flex gap-2">
      <Button
       variant="outline"
       onClick={handleExportCatalog}
      >
       {t("admin.products.exportCatalog") || "Export Catalog"}
      </Button>
      <Button
       variant="outline"
       onClick={() => {
        setShowImportModal(true);
        // If there's an active job, resume polling
        if (importJobId && !pollingTimeoutRef.current) {
         const status = importProgress?.status;
         if (status === "running" || status === "pending") {
          setImporting(true);
          pollImportStatus(importJobId);
         }
        }
       }}
      >
       {importJobId && importProgress
        ? importProgress.status === "running" || importProgress.status === "pending"
          ? `${t("admin.products.importProducts")} (${t("admin.products.inProgress")})`
          : importProgress.status === "cancelled"
          ? `${t("admin.products.importProducts")} (${t("admin.products.cancelled")})`
          : t("admin.products.importProducts")
        : t("admin.products.importProducts")}
      </Button>
      <Button onClick={handleCreate}>{t("admin.products.addProduct")}</Button>
     </div>
    </div>

    {error && <Alert variant="error">{error}</Alert>}
    {success && <Alert variant="success">{success}</Alert>}

    <Card padding="lg">
     <Stack spacing="md">
      {selectedIds.size > 0 && (
       <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 ">
        <div className="text-sm font-medium text-blue-900">
         {t("admin.products.selectedCount", { count: selectedIds.size })}
        </div>
        <div className="flex gap-2 items-center">
         <select
          onChange={(e) => {
           const value = e.target.value;
           handleBulkAction(value);
           // Reset select to placeholder after action
           e.target.value = "";
          }}
          disabled={bulkActionLoading}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded bg-white min-w-[180px]"
          defaultValue=""
         >
          <option value="" disabled>{t("admin.products.selectBulkAction") || "Select action..."}</option>
          <option value="" disabled>--- {t("admin.products.changeStatus")} ---</option>
          <option value="active">{t("admin.products.setToActive")}</option>
          <option value="draft">{t("admin.products.setToDraft")}</option>
          <option value="archived">{t("admin.products.setToArchived")}</option>
          <option value="" disabled>--- {t("admin.products.otherActions") || "Other Actions"} ---</option>
          <option value="duplicate">{t("admin.products.duplicateSelected")}</option>
          <option value="delete" className="text-red-600">{t("admin.products.deleteSelected")}</option>
         </select>
        </div>
       </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
       <div key="search-input-wrapper">
        <SearchInput
         key="admin-products-search-input"
         instanceId="admin-products-search-input"
         initialValue=""
         onSearchReady={handleSearchReady}
         debounceMs={500}
         placeholder={searchPlaceholder}
        />
       </div>
       <Select
        options={[
         { value: "", label: t("admin.products.allStatuses") },
         { value: "active", label: t("admin.active") },
         { value: "draft", label: t("admin.draft") },
         { value: "archived", label: t("admin.archived") },
        ]}
        value={statusFilter}
        onChange={(e) => {
         setStatusFilter(e.target.value);
         setPage(1);
        }}
        searchable={false}
        fullWidth
       />
       <Select
        options={[
         { value: "", label: t("admin.products.allCountries") },
         ...availableCountries.map((country) => ({
          value: country.code,
          label: country.name,
         })),
        ]}
        value={countryFilter}
        onChange={(e) => {
         setCountryFilter(e.target.value);
         setPage(1);
        }}
        searchable={false}
        fullWidth
       />
      </div>

      <div className="overflow-x-auto">
       <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
         <tr>
          <th className="w-10 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
           <input
            type="checkbox"
            checked={selectedIds.size > 0 && selectedIds.size === products.length}
            onChange={(e) => handleSelectAll(e.target.checked)}
            className="rounded border-gray-300"
           />
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-64 max-w-64">
           {t("admin.products.productName")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">
           {t("admin.products.price")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-28">
           {t("admin.products.inventory")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32">
           {t("admin.status")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">
           Featured
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-28">
           {t("admin.created")}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase w-40">
           {t("admin.actions")}
          </th>
         </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
         {products.length === 0 ? (
          <tr>
           <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
            {t("admin.products.noProducts")}
           </td>
          </tr>
         ) : (
         products.map((product) => {
          const image = product.images?.find((img) => img.isPrimary) || product.images?.[0];
          return (
           <tr key={product._id}>
            <td className="px-6 py-4 whitespace-nowrap">
             <input
              type="checkbox"
              checked={selectedIds.has(product._id)}
              onChange={(e) => handleSelectOne(product._id, e.target.checked)}
              className="rounded border-gray-300"
             />
            </td>
            <td 
             className="px-6 py-4 w-64 max-w-64 cursor-pointer hover:bg-gray-50 transition-colors"
             onClick={() => handleEdit(product._id)}
            >
             <div className="flex items-center gap-3 min-w-0">
              {image && (() => {
               const thumbnailUrl = getThumbnailUrl(image.url);
               const originalUrl = getOriginalImageUrl(image.url);
               
               return (
                <img
                 src={thumbnailUrl}
                 alt={product.title}
                 className="w-10 h-10 object-cover rounded flex-shrink-0"
                 onError={(e) => {
                  // Fallback to original image if thumbnail fails
                  const target = e.target as HTMLImageElement;
                  if (target.src !== originalUrl) {
                   target.src = originalUrl;
                  }
                 }}
                />
               );
              })()}
              <div className="min-w-0 flex-1">
               <div 
                className="text-sm font-medium text-gray-900 truncate" 
                title={product.title}
               >
                {product.title}
               </div>
               <div className="text-xs text-gray-500">
                {t("admin.products.optionGroups", { count: product.variantOptions?.length || 0 })}
               </div>
              </div>
             </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-24">
             <Price 
              amount={product.displayPrice !== undefined ? product.displayPrice : (product.basePrice || 0)} 
              currency={product.displayCurrency || currency} 
              size="sm" 
             />
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-28">
             {(() => {
              // If product has variantOptions, calculate total inventory from option values
              if (product.variantOptions && product.variantOptions.length > 0) {
               // Sum inventory from all option values
               let totalInventory = 0;
               for (const option of product.variantOptions) {
                for (const value of option.values) {
                 if (value.inventoryQuantity !== undefined && value.inventoryQuantity !== null) {
                  totalInventory += value.inventoryQuantity;
                 }
                }
               }
               return totalInventory > 0 ? totalInventory : <span className="text-gray-400 italic text-xs">{t("admin.products.variantBased")}</span>;
              }
              // Otherwise show base inventory
              // Check if inventoryQuantity exists and is a valid number
              const inventory = product.inventoryQuantity;
              
              // Only show if it's explicitly set (not undefined, not null)
              // Note: 0 is a valid inventory value, so we need to distinguish
              // between "not set" (undefined/null) and "set to 0"
              if (inventory !== undefined && inventory !== null) {
               // It's set - display it (even if it's 0)
               return inventory;
              }
              
              // Not set - show dash
              return <span className="text-gray-400">—</span>;
             })()}
            </td>
            <td className="px-6 py-4 whitespace-nowrap w-32">
             <select
              value={product.status}
              onChange={(e) =>
               handleStatusChange(product._id, e.target.value)
              }
              className="px-2 py-1 text-xs font-semibold border border-gray-300"
             >
              <option value="active">{t("admin.active")}</option>
              <option value="draft">{t("admin.draft")}</option>
              <option value="archived">{t("admin.archived")}</option>
             </select>
            </td>
            <td className="px-6 py-4 whitespace-nowrap w-24">
             <input
              type="checkbox"
              checked={product.featured || false}
              onChange={(e) => handleFeaturedChange(product._id, e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              title={product.featured ? "Unfeature product" : "Feature product"}
             />
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-28">
             {new Date(product.createdAt).toLocaleDateString()}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2 w-40">
             <div className="flex gap-1">
{/*                <Button
                variant="outline"
                size="sm"
                onClick={() => handleEdit(product._id)}
               >
                {t("admin.edit")}
               </Button> */}
               <Button
                variant="outline"
                size="sm"
                onClick={() => handleShowQRCode(product._id, product.title)}
                title="Generate QR Code"
               >
                QR
               </Button>
              <Button
               variant="outline"
               size="sm"
               onClick={() => handleDelete(product._id)}
               className="text-red-600 hover:text-red-700 w-full"
              >
               {t("admin.delete")}
              </Button>
             </div>
            </td>
           </tr>
          );
         }))}
        </tbody>
       </table>
      </div>

      {totalPages > 1 && (
       <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500">
         {t("admin.products.showing", { start: ((page - 1) * limit) + 1, end: Math.min(page * limit, total), total })}
        </div>
        <div className="flex gap-2">
         <Button
          variant="outline"
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
         >
          {t("common.previous")}
         </Button>
         <div className="flex items-center gap-2">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
           const pageNum = i + 1;
           return (
            <Button
             key={pageNum}
             variant={page === pageNum ? "primary" : "outline"}
             size="sm"
             onClick={() => setPage(pageNum)}
            >
             {pageNum}
            </Button>
           );
          })}
         </div>
         <Button
          variant="outline"
          disabled={page === totalPages}
          onClick={() => setPage(page + 1)}
         >
          {t("common.next")}
         </Button>
        </div>
       </div>
      )}
     </Stack>
    </Card>
   </Stack>

   {/* Product Form Modal */}
   <ProductFormModal
    isOpen={showProductModal}
    onClose={handleModalClose}
    productId={editingProductId}
    onSuccess={handleProductSaved}
   />

   {/* QR Code Modal */}
   {qrCodeProductId && (
    <ProductQRCodeModal
     isOpen={showQRCodeModal}
     onClose={() => {
      setShowQRCodeModal(false);
      setQrCodeProductId(null);
      setQrCodeProductTitle("");
     }}
     productId={qrCodeProductId}
     productTitle={qrCodeProductTitle}
    />
   )}

   {/* CSV Import Modal */}
   <Modal
    isOpen={showImportModal}
    onClose={() => {
     // Always allow closing - job continues in background
     // Stop polling when modal closes (but keep jobId in localStorage)
     if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
     }
     setShowImportModal(false);
     // Don't clear importJobId or importProgress - keep them for when modal reopens
     // Only clear file and preview if import hasn't started
     if (!importJobId) {
      setImportFile(null);
      setImportPreview([]);
     }
    }}
    title={t("admin.products.importModalTitle")}
    size="lg"
   >
    <div className="space-y-4">
     {error && <Alert variant="error">{error}</Alert>}
     {success && <Alert variant="success">{success}</Alert>}

     {importJobId && importProgress && (
      <Alert variant="info">
       {t("admin.products.importResumed", {
        processed: importProgress.processed,
        total: importProgress.total,
       })}
      </Alert>
     )}

     {!importJobId && (
      <>
       <div>
        <div className="flex items-center justify-between mb-2">
         <label className="block text-sm font-medium text-gray-700">
          {t("admin.products.selectCsvFile")}
         </label>
         <Button
          variant="outline"
          size="sm"
          onClick={handleDownloadTemplate}
          type="button"
         >
          {t("admin.products.downloadTemplate")}
         </Button>
        </div>
        <input
         type="file"
         accept=".csv"
         onChange={handleFileSelect}
         className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
         disabled={importing}
        />
       </div>
       <div className="text-sm text-gray-600">
        <p className="mb-2">{t("admin.products.csvFormatHint")}</p>
        <p>{t("admin.products.imagesAutoDownload")}</p>
       </div>
      </>
     )}

     {importPreview.length > 0 && !importJobId && (
      <div className="border rounded-lg overflow-hidden">
       <div className="bg-gray-50 px-4 py-2 border-b">
        <p className="text-sm font-medium text-gray-700">
         {t("admin.products.previewRecords", { count: importPreview.length })}
        </p>
       </div>
       <div className="max-h-96 overflow-y-auto">
        <table className="min-w-full divide-y divide-gray-200">
         <thead className="bg-gray-50 sticky top-0">
          <tr>
           <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t("admin.products.productName")}
           </th>
           <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Handle
           </th>
          </tr>
         </thead>
         <tbody className="bg-white divide-y divide-gray-200">
          {importPreview.map((record, index) => (
           <tr key={index}>
            <td className="px-4 py-2 text-sm text-gray-900">{record.title}</td>
            <td className="px-4 py-2 text-sm text-gray-500">{record.handle}</td>
           </tr>
          ))}
         </tbody>
        </table>
       </div>
      </div>
     )}

     {importProgress && (
      <div className="space-y-4">
       <div className="bg-gray-50 px-4 py-3 rounded-lg">
        <div className="flex items-center justify-between mb-2">
         <span className="text-sm font-medium text-gray-700">
          {t("admin.products.importProgress")}
         </span>
         <span className="text-sm text-gray-600">
          {importProgress.processed} / {importProgress.total}
         </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
         <div
          className={`h-2 rounded-full transition-all duration-300 ${
           importProgress.status === "cancelled"
            ? "bg-yellow-600"
            : importProgress.status === "failed"
            ? "bg-red-600"
            : "bg-blue-600"
          }`}
          style={{
           width: `${importProgress.total > 0 ? (importProgress.processed / importProgress.total) * 100 : 0}%`,
          }}
         />
        </div>
        <div className="mt-2 text-xs text-gray-600">
         {t("admin.products.imported")}: {importProgress.imported} |{" "}
         {t("admin.products.skipped")}: {importProgress.skipped} |{" "}
         {t("admin.products.errorCount")}: {importProgress.records.filter((r) => r.status === "error").length}
        </div>
       </div>

       <div className="border rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-2 border-b">
         <p className="text-sm font-medium text-gray-700">
          {t("admin.products.importStatus")}
         </p>
        </div>
        <div className="max-h-96 overflow-y-auto">
         <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0">
           <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
             {t("admin.products.productName")}
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
             Status
            </th>
           </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
           {importProgress.records.map((record, index) => (
            <tr key={index}>
             <td className="px-4 py-2 text-sm text-gray-900">{record.title}</td>
             <td className="px-4 py-2 text-sm">
              {record.status === "pending" && (
               <span className="text-gray-500">{t("admin.products.pending")}</span>
              )}
              {record.status === "processing" && (
               <span className="text-blue-600 flex items-center gap-1">
                <Spinner size="sm" />
                {t("admin.products.processing")}
               </span>
              )}
              {record.status === "completed" && (
               <span className="text-green-600">{t("admin.products.completed")}</span>
              )}
              {record.status === "error" && (
               <span className="text-red-600" title={record.error}>
                {t("admin.products.error")}
               </span>
              )}
              {importProgress.status === "cancelled" && record.status === "pending" && (
               <span className="text-gray-400">{t("admin.products.cancelled")}</span>
              )}
             </td>
            </tr>
           ))}
          </tbody>
         </table>
        </div>
       </div>
      </div>
     )}

     <div className="flex gap-2 justify-end pt-4 border-t">
      {importJobId &&
       importProgress &&
       (importProgress.status === "running" || importProgress.status === "pending") && (
        <Button variant="outline" onClick={handleAbortImport} disabled={!importJobId}>
         {t("admin.products.abortImport")}
        </Button>
       )}
      <Button
       variant="outline"
       onClick={() => {
        // Always allow closing - job continues in background
        // Stop polling when modal closes (but keep jobId in localStorage)
        if (pollingTimeoutRef.current) {
         clearTimeout(pollingTimeoutRef.current);
         pollingTimeoutRef.current = null;
        }
        setShowImportModal(false);
        // Don't clear importJobId or importProgress - keep them for when modal reopens
        // Only clear file and preview if import hasn't started
        if (!importJobId) {
         setImportFile(null);
         setImportPreview([]);
        }
       }}
      >
       {importJobId && importProgress?.status === "completed" ? t("common.close") : t("common.cancel")}
      </Button>
      {!importJobId && (
       <Button onClick={handleStartImport} disabled={!importFile || importPreview.length === 0 || importing}>
        {importing ? <Spinner size="sm" /> : t("admin.products.startImport")}
       </Button>
      )}
     </div>
    </div>
   </Modal>
  </Container>
 );
};

export default Products;
